import {
  useEditor,
  type NodeTree,
  type Node,
  type NodeId,
  type SerializedNodes,
  type SerializedNode,
  Element as CraftElement,
} from '@craftjs/core';
import { getElementDimensionsWithoutPadding } from '@/utils/numToMeasurement';
import type { ImageProps } from '@builder4/shared/components/Image';
import { useCopyPasteHelper } from './useCopyPasteHelper';
import {
  emptyContainer,
  getBoundaryDomElement,
  isContainerNode,
  isIconNode,
  isImageNode,
  isRelativelyFillableElementV2,
  isSingleRootContainer,
  getNextPopupNumber,
} from '@/utils/helperFunc';
import { getRandomId, ROOT_NODE } from '@craftjs/utils';
import type { ContainerProps } from '@builder4/shared/components/Container';
import { useContainerHelper } from './useContainerHelper';
import { addSentryBreadcrumb } from '@/utils/sentry';
import { BUTTON_NODE_NAME, CONTENT_BLOCK_NAME, DUPLICATE_EVENT } from '@/utils/constants';
import { setNodeHidden, getNodeHidden } from '@/utils/nodeVisibilityUtils';
import { clearFormIdentifiersFromNode } from '@/utils/formDuplicationUtils';
import {
  getBreakpointProp,
  setBaseProp,
  setBreakpointProp,
} from '@builder4/shared/utils/breakpointUtils';
import { useAppSelector } from '@/store/hooks';
import { selectBreakpoint } from '@/store/system/selectors';
import type { Breakpoint } from '@builder4/shared/types/layout';
import EditorPopup from '@/components/userComponents/Popup';
import EditorContainer from '@/components/userComponents/Container';

export const useCraftHelper = () => {
  const { query, actions } = useEditor();
  const breakpoint = useAppSelector(selectBreakpoint);
  const {
    reAdustFillableObjectSpace,
    setInitialPlaceholderHeight,
    getFillableChilds,
    calculateDistributedWidths,
  } = useContainerHelper();
  const { copyNodeTree, pasteNodeTree } = useCopyPasteHelper();

  const getIndexInSiblings = (id: string, parentId: string) => {
    const siblings = query.node(parentId).get().data.nodes;
    return siblings.indexOf(id);
  };

  const getChildrenExcept = (parentId: NodeId, id: NodeId): Node[] => {
    const siblings = query.node(parentId).get().data.nodes;
    return siblings
      .filter((siblingId: NodeId) => siblingId !== id)
      .map((siblingId: NodeId) => query.node(siblingId).get()) as Node[];
  };

  const cloneNodeTree = (idToClone: string) => {
    const tree = query.node(idToClone).toNodeTree();
    const newNodes: Record<string, Node> = {};

    const changeNodeId = (node: Node, newParentId?: string) => {
      const newNodeId = getRandomId();
      const childNodes = node.data.nodes.map((childId: string) =>
        changeNodeId(tree.nodes[childId], newNodeId),
      );
      const linkedNodes = Object.keys(node.data.linkedNodes).reduce((accum, id) => {
        const linkedNodeId = changeNodeId(tree.nodes[node.data.linkedNodes[id]], newNodeId);
        return {
          ...accum,
          [id]: linkedNodeId,
        };
      }, {});

      const tmpNode = {
        ...node,
        id: newNodeId,
        data: {
          ...node.data,
          parent: newParentId || node.data.parent,
          nodes: childNodes,
          linkedNodes,
        },
      };
      const freshnode = query.parseFreshNode(tmpNode).toNode();
      // Clear unique form and field identifiers
      const clearedNode = clearFormIdentifiersFromNode(freshnode);
      newNodes[newNodeId] = clearedNode;
      return newNodeId;
    };

    const rootNodeId = changeNodeId(tree.nodes[tree.rootNodeId]);
    return {
      rootNodeId,
      nodes: newNodes,
    } as NodeTree;
  };

  const getSectionNodeTreeWithUniqueIds = (sectionData: {
    rootNodeId: string;
    nodes: SerializedNodes;
  }) => {
    const { rootNodeId, nodes } = sectionData;

    // Create ID mapping for all VALID nodes (skip undefined or malformed)
    const idMap: Record<string, string> = {};
    const validNodeIds = Object.keys(nodes).filter((oldId) => {
      const n = nodes[oldId] as SerializedNode;
      return n && n.type; // craft serialized node must have type
    });
    validNodeIds.forEach((oldId) => {
      idMap[oldId] = getRandomId();
    });

    // Convert serialized nodes with properly remapped relationships
    const nodePairs: [string, Node][] = [];
    for (const oldId of validNodeIds) {
      const newId = idMap[oldId];
      const serializedNode = nodes[oldId];
      if (!newId || !serializedNode) continue;
      try {
        const parsed = query.parseSerializedNode(serializedNode).toNode((node) => {
          node.id = newId;

          // Update parent reference
          if (node.data.parent && idMap[node.data.parent]) {
            node.data.parent = idMap[node.data.parent];
          }

          // Update child nodes array - this is crucial for nested structures
          if (Array.isArray(node.data.nodes) && node.data.nodes.length > 0) {
            node.data.nodes = node.data.nodes
              .map((childId: string) => idMap[childId])
              .filter(Boolean) as string[];
          }

          // Update linked nodes
          if (node.data.linkedNodes && typeof node.data.linkedNodes === 'object') {
            node.data.linkedNodes = Object.fromEntries(
              Object.entries(node.data.linkedNodes)
                .map(([key, val]) => (idMap[val as string] ? [key, idMap[val as string]] : null))
                .filter(Boolean) as [string, string][],
            );
          }
        });
        // Clear unique form and field identifiers when adding saved sections
        const clearedNode = clearFormIdentifiersFromNode(parsed);
        nodePairs.push([newId, clearedNode]);
      } catch (e) {
        // Skip nodes that fail to parse (e.g., unknown component types)
        console.log('Failed to parseSerializedNode, skipping node', e);
      }
    }

    const remappedRoot = idMap[rootNodeId];
    const nodesObj = Object.fromEntries(nodePairs);
    const tree: NodeTree = {
      rootNodeId: remappedRoot && nodesObj[remappedRoot] ? remappedRoot : nodePairs[0]?.[0],
      nodes: nodesObj,
    };

    return tree;
  };

  const getRootInsertIndex = (selectedNodeId?: string | null): number | undefined => {
    if (!selectedNodeId || selectedNodeId === ROOT_NODE) return undefined;
    let currentNodeId = selectedNodeId;
    let currentNode = query.node(currentNodeId).get();
    if (!currentNode) return undefined;

    while (currentNode.data.parent && currentNode.data.parent !== ROOT_NODE) {
      currentNodeId = currentNode.data.parent as string;
      currentNode = query.node(currentNodeId).get();
      if (!currentNode) return undefined;
    }

    const rootNode = query.node(ROOT_NODE).get();
    const rootChildren = rootNode?.data?.nodes ?? [];
    const currentIndex = rootChildren.indexOf(currentNodeId);
    return currentIndex === -1 ? undefined : currentIndex + 1;
  };

  const addSectionToRoot = (nodeTree: NodeTree, selectedNodeId?: string | null) => {
    const rootNodes = query.node(ROOT_NODE).get()?.data?.nodes ?? [];
    let addNodeTree = actions.addNodeTree;

    //remove empty root container if it is the only child of the root node to replace it with the new section
    if (rootNodes.length === 1) {
      const rootContainer = query.node(rootNodes[0]).get() as Node;
      if (isContainerNode(rootContainer) && rootContainer.data.nodes.length === 0) {
        actions.delete(rootNodes[0]);
        addNodeTree = actions.history.merge().addNodeTree;
      }
    }

    const insertIndex = getRootInsertIndex(selectedNodeId);
    addNodeTree(nodeTree, ROOT_NODE, insertIndex);
    actions.selectNode(nodeTree.rootNodeId);

    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(() => {
        query.node(nodeTree.rootNodeId).get()?.dom?.scrollIntoView({ block: 'nearest' });
      });
    }
  };

  const addPopup = () => {
    const nodeTree = query
      .parseReactElement(
        <CraftElement canvas is={EditorPopup}>
          <CraftElement canvas is={EditorContainer} height="100%" />
        </CraftElement>,
      )
      .toNodeTree();
    if (nodeTree) {
      const nextNumber = getNextPopupNumber(query);
      actions.addNodeTree(nodeTree, ROOT_NODE);
      actions.setCustom(nodeTree.rootNodeId, (custom) => {
        custom.displayName = `Popup ${nextNumber}`;
      });
      setNodeDimensionsOnCreation(nodeTree);
    }
    return nodeTree?.rootNodeId;
  };

  const isResizable = (id: string) => {
    return (
      isContainerNode(query.node(id)?.get() as Node) || isImageNode(query.node(id)?.get() as Node)
    );
  };

  /**
   * Calculates the initial placeholder height for a new node (such as a container or image)
   * based on its parent node's properties and layout.
   *
   * - If the parent is the ROOT_NODE, returns 0 (no placeholder height).
   * - If the parent has flexDirection 'row' or is empty (no siblings except the new node),
   *   returns the parent's content height (from getNodeContentDimensions).
   * - Otherwise, returns 0.
   *
   * @param parentId - The ID of the parent node.
   * @param id - (Optional) The ID of the new node being added.
   * @returns The calculated placeholder height in pixels.
   */
  const getInitialPlaceholderHeight = (parentId: string, id?: NodeId) => {
    const parentNode = query.node(parentId)?.get();
    if (!parentNode) return 0;

    if (parentId === ROOT_NODE) {
      return 0;
    }
    //if fixed height and row or sibling
    //image and container are the only elements that can have a placeholder height fornow
    let height = 0;
    const isEmpty = id ? getChildrenExcept(parentId, id).length === 0 : emptyContainer(parentNode);
    const flexDirection = getBreakpointProp(
      parentNode.data.props as Partial<ContainerProps>,
      'flexDirection',
      breakpoint,
    );
    if (flexDirection === 'row' || isEmpty) {
      height = getNodeContentDimensions(parentNode).height;
    }

    return height;
  };
  const fillButtonWidth = (parentId: string, newNodeId: string) => {
    const widths = calculateDistributedWidths(parentId, newNodeId);
    if (!widths) return;

    const { newNodeWidth, prevSiblingWidth, nextSiblingWidth, prevSiblingId, nextSiblingId } =
      widths;

    const methods = actions.history.merge();
    methods.setProp(newNodeId, (props: Partial<ContainerProps>) => {
      const newNode = query.node(newNodeId).get();
      setBreakpointProp(
        props,
        'width',
        `${newNodeWidth}%`,
        breakpoint,
        newNode?.data.displayName || BUTTON_NODE_NAME,
      );
      setBreakpointProp(
        props,
        'flexBasis',
        `${newNodeWidth}%`,
        breakpoint,
        newNode?.data.displayName || BUTTON_NODE_NAME,
      );
    });
    if (prevSiblingId) {
      methods.setProp(prevSiblingId, (props: Partial<ContainerProps>) => {
        const prevSibling = query.node(prevSiblingId).get();
        setBreakpointProp(
          props,
          'width',
          `${prevSiblingWidth}%`,
          breakpoint,
          prevSibling?.data.displayName || CONTENT_BLOCK_NAME,
        );
        setBreakpointProp(
          props,
          'flexBasis',
          `${prevSiblingWidth}%`,
          breakpoint,
          prevSibling?.data.displayName || CONTENT_BLOCK_NAME,
        );
      });
    }
    if (nextSiblingId) {
      methods.setProp(nextSiblingId, (props: Partial<ContainerProps>) => {
        const nextSibling = query.node(nextSiblingId).get();
        setBreakpointProp(
          props,
          'width',
          `${nextSiblingWidth}%`,
          breakpoint,
          nextSibling?.data.displayName || CONTENT_BLOCK_NAME,
        );
        setBreakpointProp(
          props,
          'flexBasis',
          `${nextSiblingWidth}%`,
          breakpoint,
          nextSibling?.data.displayName || CONTENT_BLOCK_NAME,
        );
      });
    }
  };
  /**
   * Returns the content dimensions (height and width) no padding or border width included of a given Craft.js node.
   * If the node has a DOM element, it finds the boundary node and measures its dimensions.
   * If the node does not have a DOM element, returns {height: 0, width: 0}.
   *
   * @param node - The Craft.js node to measure.
   * @returns An object containing the height and width in pixels.
   */
  const getNodeContentDimensions = (node: Node): { height: number; width: number } => {
    if (!node.dom) return { height: 0, width: 0 };
    const boundaryDiv = getBoundaryDomElement(node.dom as HTMLElement);
    let dimensions = { height: 0, width: 0 };
    if (boundaryDiv) {
      dimensions = getElementDimensionsWithoutPadding(boundaryDiv);
    }
    return dimensions;
  };

  const setImageInitialPlaceholder = (placeholderHeight: number, imageNodeId: string) => {
    if (placeholderHeight) {
      actions.history.ignore().setProp(imageNodeId, (props: Partial<ImageProps>) => {
        props.height = `${placeholderHeight}px`;
      });
    }
  };

  const showNodeAllDevices = (id: string) => {
    if (id === ROOT_NODE) return;
    const node = query.node(id).get();
    const children = query.node(id).descendants(true);

    setNodeHidden(actions, id, node.data.displayName, false);

    children.forEach((childId) => {
      const childNode = query.node(childId).get();
      setNodeHidden(actions.history.merge(), childId, childNode.data.displayName, false);
    });
  };
  const lockLayer = (id: string) => {
    if (id === ROOT_NODE) return;
    const children = query.node(id).descendants(true);
    actions.setCustom(id, (custom) => {
      custom.isLocked = true;
    });
    children.forEach((childId) => {
      actions.history.merge().setCustom(childId, (custom) => {
        custom.isLocked = true;
      });
    });
  };

  const toggleLock = (id: NodeId): boolean => {
    if (id === ROOT_NODE) return false;
    const newValue = !query.node(id).get().data.custom.isLocked;
    if (newValue) {
      lockLayer(id);
    } else {
      unlockLayer(id);
    }
    return newValue;
  };

  const removeNode = (id: string) => {
    if (!id || id === ROOT_NODE) return;

    const node = query.node(id).get();
    if (!node) return;

    if (isSingleRootContainer(node, query)) {
      return;
    }

    addSentryBreadcrumb('Element deleted', {
      nodeId: id,
      nodeType: node.data.displayName || node.data.type,
    });

    actions.delete(id);
  };
  const hideNodeAllDevices = (id: string) => {
    if (id === ROOT_NODE) return;
    const node = query.node(id).get();
    const children = query.node(id).descendants(true);

    setNodeHidden(actions, id, node.data.displayName, true);

    children.forEach((childId) => {
      const childNode = query.node(childId).get();
      setNodeHidden(actions.history.merge(), childId, childNode.data.displayName, true);
    });
  };

  const unlockLayer = (id: string) => {
    if (id === ROOT_NODE) return;

    const children = query.node(id).descendants(true);
    //do not unlock if any parent is locked
    const ancestors = query.node(id).ancestors(true);
    const lockedParent = ancestors.find((ancestorId) => {
      const parent = query.node(ancestorId).get();
      if (parent.data.custom.isLocked) {
        return true;
      }
      return false;
    });

    if (lockedParent) {
      return;
    }

    actions.setCustom(id, (custom) => {
      custom.isLocked = false;
    });
    children.forEach((childId) => {
      actions.history.merge().setCustom(childId, (custom) => {
        custom.isLocked = false;
      });
    });
  };

  const toggleVisibility = (id: NodeId): boolean => {
    if (id === ROOT_NODE) return false;
    const node = query.node(id).get();
    const currentHidden = getNodeHidden(
      node.data.displayName,
      node.data.props?.hidden as boolean | undefined,
      node.data.hidden,
    );
    const newValue = !currentHidden;
    if (newValue) {
      hideNodeAllDevices(id);
    } else {
      showNodeAllDevices(id);
    }
    return newValue;
  };

  const duplicateNode = (id: NodeId): NodeId | null => {
    if (id === ROOT_NODE) return null;
    const targetNode = query.node(id).get();
    addSentryBreadcrumb('Element duplicated', {
      nodeId: id,
      nodeType: targetNode.data.displayName || targetNode.data.type,
    });
    const parentId = targetNode.data.parent;
    const newTree = cloneNodeTree(id);
    const newIndex = getIndexInSiblings(id, parentId as string);
    actions.addNodeTree(
      { ...newTree, eventSource: DUPLICATE_EVENT },
      parentId as string,
      newIndex + 1,
    );
    actions.selectNode(newTree.rootNodeId);
    return newTree.rootNodeId;
  };

  const copyNode = (id: NodeId): void => {
    copyNodeTree(id);
  };

  const pasteNode = async (targetNodeId: NodeId): Promise<Node | false> => {
    const newNodes = await pasteNodeTree(targetNodeId);
    if (newNodes && newNodes.length > 0) {
      ['desktop', 'tablet', 'mobile'].forEach((bp) => {
        newNodes.forEach((newNode) => {
          actions.history.merge().setProp(newNode.id, (props: Partial<ContainerProps>) => {
            const parentNode = query.node(newNode.data.parent as string).get();
            const parentFlexDirection = getBreakpointProp(
              parentNode.data.props as Partial<ContainerProps>,
              'flexDirection',
              bp as Breakpoint,
            );
            if (parentFlexDirection === 'row') {
              setBreakpointProp(
                props,
                'fillSpace',
                'yes',
                bp as Breakpoint,
                newNode.data.displayName,
              );
              setBreakpointProp(props, 'flexGrow', 1, bp as Breakpoint, newNode.data.displayName);
            } else {
              setBreakpointProp(
                props,
                'fillSpace',
                'no',
                bp as Breakpoint,
                newNode.data.displayName,
              );
            }
            if (
              getChildrenExcept(newNode.data.parent as string, newNode.id).length === 0 ||
              parentFlexDirection === 'column'
            ) {
              setBreakpointProp(props, 'width', '100%', bp as Breakpoint, newNode.data.displayName);
            }
          });

          reAdustFillableObjectSpace(targetNodeId, newNode.id as string, bp as Breakpoint);
        });
      });
    }
    return newNodes && newNodes.length > 0 ? newNodes[0] : false;
  };

  const setNodeDimensionsOnCreation = (nodeTree: NodeTree) => {
    const newNode = query.node(nodeTree.rootNodeId).get();
    const parentNode = query.node(newNode.data.parent as string).get();

    if (isContainerNode(newNode)) {
      if (newNode.data.parent === ROOT_NODE) {
        actions.history.ignore().setProp(newNode.id as string, (props: Partial<ContainerProps>) => {
          //set base prop so other breakpoints will inherit the value
          setBaseProp(props, 'flexDirection', 'row', newNode.data.displayName);
          setBaseProp(props, 'disableMaxWidth', true, newNode.data.displayName);
        });
      }

      const placeholderHeight = getInitialPlaceholderHeight(
        newNode.data.parent as string,
        newNode.id,
      );

      setInitialPlaceholderHeight(placeholderHeight, newNode.id as string);
    } else if (isImageNode(newNode)) {
      const placeholderHeight = getInitialPlaceholderHeight(
        newNode.data.parent as string,
        newNode.id,
      );
      setImageInitialPlaceholder(placeholderHeight, newNode.id as string);
    }

    // Icon elements should keep their explicit pixel dimensions (e.g. 48x48)
    // and not be stretched to fill the parent like Image/Container elements.
    if (isIconNode(newNode)) {
      actions.selectNode(newNode.id);
      return;
    }

    ['desktop', 'tablet', 'mobile'].forEach((bp) => {
      //element will be fillable in all breakpoints
      if (isRelativelyFillableElementV2(newNode as Node, bp as Breakpoint)) {
        // there may be different fillable childs in different breakpoints
        const fillableSiblings = getFillableChilds(newNode.data.parent as string, bp as Breakpoint);

        //parent flex direction may be different in different breakpoints
        const parentFlexDirection = getBreakpointProp(
          parentNode.data.props as Partial<ContainerProps>,
          'flexDirection',
          bp as Breakpoint,
        );

        const fillSpace = parentFlexDirection === 'row' ? 'yes' : 'no';

        actions.history.ignore().setProp(newNode.id as string, (props: Partial<ContainerProps>) => {
          setBreakpointProp(
            props,
            'fillSpace',
            fillSpace,
            bp as Breakpoint,
            newNode.data.displayName,
          );

          const siblings = fillableSiblings.filter((child) => child.id !== newNode.id);

          if (parentFlexDirection === 'row' && siblings.length > 0) {
            const sibling = siblings[0];
            const childWidth = getBreakpointProp(
              sibling.data.props as Partial<ContainerProps>,
              'width',
              bp as Breakpoint,
            );
            const childFlexBasis = getBreakpointProp(
              sibling.data.props as Partial<ContainerProps>,
              'flexBasis',
              bp as Breakpoint,
            );

            setBreakpointProp(
              props,
              'width',
              childWidth,
              bp as Breakpoint,
              newNode.data.displayName,
            );
            setBreakpointProp(
              props,
              'flexBasis',
              childFlexBasis,
              bp as Breakpoint,
              newNode.data.displayName,
            );
          } else {
            setBreakpointProp(props, 'width', '100%', bp as Breakpoint, newNode.data.displayName);
            setBreakpointProp(
              props,
              'flexBasis',
              '100%',
              bp as Breakpoint,
              newNode.data.displayName,
            );
          }
        });

        //as parent flex direction is breakpoint specific so there is chance that we do not re adjust
        //space in all breakpoints cases.
        if (newNode.data.parent !== ROOT_NODE && parentFlexDirection === 'row') {
          reAdustFillableObjectSpace(
            newNode.data.parent as string,
            newNode.id as string,
            bp as Breakpoint,
          );
        }
      }
    });
    actions.selectNode(newNode.id);
  };

  return {
    getInitialPlaceholderHeight,
    setImageInitialPlaceholder,
    isResizable,
    cloneNodeTree,
    getIndexInSiblings,
    getNodeContentDimensions,
    removeNode,
    toggleVisibility,
    hideNodeAllDevices,
    showNodeAllDevices,
    duplicateNode,
    toggleLock,
    copyNode,
    pasteNode,
    getChildrenExcept,
    getSectionNodeTreeWithUniqueIds,
    addSectionToRoot,
    addPopup,
    setNodeDimensionsOnCreation,
    fillButtonWidth,
  };
};
